import { cn } from "@/lib/utils";
import { useI18N } from "common/hooks";
import { AccessibilityCategory, VenueDetails } from "common/types";
import AccessibilityItem from "@/features/home/VenuePreview/AccessibilityItem";
import { useEffect, useMemo, useRef, useState } from "react";
import ScrollContainer from "react-indiana-drag-scroll";
import { formatDateShort } from "common/utils/general";
import EmptyAccessibility from "@/assets/images/empty-accessibility.svg";
import AnalyticsService from "@/services/analytics";

type AccessibilitySectionProps = {
  venueDetails: VenueDetails;
  accessibilityCategories: AccessibilityCategory[];
  fullSize?: boolean;
  initialSection?: string;
  onLogin: () => void;
};

function AccessibilitySection({
  venueDetails,
  accessibilityCategories,
  fullSize,
  initialSection,
  onLogin,
}: AccessibilitySectionProps) {
  const i18n = useI18N();
  const accessibilitySectionRefs = useRef(new Map());

  const [selectedSection, setSelectedSection] = useState(
    initialSection ?? "All"
  );

  const accessibilitySections = ["All"].concat(
    accessibilityCategories?.map((category) => category.catName) || []
  );

  const selectedSections = useMemo(() => {
    if (selectedSection === "All") {
      return accessibilityCategories;
    }
    return accessibilityCategories.filter(
      (category) => category.catName === selectedSection
    );
  }, [selectedSection, accessibilityCategories]);

  useEffect(() => {
    if (initialSection) {
      setSelectedSection(initialSection);
    }
  }, [initialSection]);

  const handleAccessibilitySectionTracking = (section) => {
    console.log("section", section);
    AnalyticsService.trackGAEventHandler(
      "navigatedToAccessibilityCategoryInformation"
    );
    AnalyticsService.trackMx("navigatedToAccessibilityCategoryInformation", {
      VenueId: venueDetails.business.id,
      VenueBrandId: venueDetails.brandId,
      VenueBrandName: venueDetails.brandName,
      VenueAddress: venueDetails.address,
      VenueName: venueDetails.name,
      VenueType: venueDetails.businessType,
      AccessibilitySection: section,
    });
  };

  return (
    <section className="px-2 md:px-0 md:mr-2">
      {accessibilitySections.length > 1 && (
        <div className="py-2">
          <ScrollContainer>
            <div className="flex flex-row space-x-4 md:space-x-6 mb-5">
              {accessibilitySections.map((section) => (
                <button
                  key={section}
                  className={cn(
                    "py-[10px] px-[16px] rounded-[20px] whitespace-nowrap text-primary-800 dark:text-primary-100 paragraph-small-medium",
                    selectedSection === section
                      ? "bg-secondary-400 dark:text-primary-900"
                      : "bg-primary-100 dark:bg-[#37354D]"
                  )}
                  onClick={() => {
                    handleAccessibilitySectionTracking(section);
                    setSelectedSection(section);
                  }}
                  aria-label={`${section} ${selectedSection === section ? "selected" : ""} `}
                >
                  {section}
                </button>
              ))}
            </div>
          </ScrollContainer>
        </div>
      )}
      

      {accessibilitySections.length > 1 &&
        venueDetails?.latestReviewDateUpdated && (
          <div
            className="mt-4 mb-5"
            tabIndex={0}
            aria-label={`${i18n.get("lastUpdated")} ${formatDateShort(venueDetails.latestReviewDateUpdated)}`}
          >
            <span
              className="text-primary-800 dark:text-primary-150 mr-2 mobile-h3-small-semi-bold"
              aria-hidden="true"
            >
              {i18n.get("lastUpdated")}:
            </span>
            <span
              className="text-primary-800 dark:text-primary-150  mr-2 paragraph-medium-medium"
              aria-hidden="true"
            >
              {formatDateShort(venueDetails.latestReviewDateUpdated)}
            </span>
          </div>
        )}

      {/* Accessibility Category & Tags */}
      <div className="flex flex-col ">
        {selectedSections?.map((category) => (
          <div
            key={category.catName}
            ref={(ref) => {
              if (ref) {
                accessibilitySectionRefs.current.set(category.catName, ref);
              }
            }}
          >
            <AccessibilityItem
              venueId={venueDetails.id}
              businessId={venueDetails.business.id.toString()}
              category={category}
              fullSize={fullSize}
              venueName={venueDetails.name}
              onLogin={() => {
                onLogin();
              }}
              venueDetails={venueDetails}
              categories={accessibilityCategories}
              lastUpdated={formatDateShort(
                venueDetails.latestReviewDateUpdated
              )}
            />
          </div>
        ))}
      </div>
      {accessibilityCategories.length === 0 && (
        <div className="items-center justify-center">
          <img
            src={EmptyAccessibility}
            alt="No search results"
            className="max-w-[70%] mx-auto my-10"
            aria-hidden="true"
          />
          <p className="text-center desktop-h2-small-bold" tabIndex={0}>
            No accessibility information yet
          </p>
          <p className="text-center paragraph-small-regular mt-3" tabIndex={0}>
            Visit the venue and use our app to tag important accessibility
            information to help the community.{" "}
          </p>
        </div>
      )}
    </section>
  );
}

export default AccessibilitySection;
